import { LngLatBounds } from "maplibre-gl";
import * as turf from "@turf/turf";
import { get, writable } from "svelte/store";
export class PolygonSelectLayerManager {
    constructor(map, enabled, settings) {
        this.beforeType = "symbol";
        this.mapLoaded = false;
        this.layersCreated = false;
        this.selectionDirty = true;
        this.map = map;
        this.selection = writable(undefined);
        this.enabled = writable(enabled);
        // make sure settings are initialized.
        settings ??= {
            beforeLayer: null,
            minZoom: 0,
            layerId: "selection-polygon"
        };
        // defaults.
        this.layerId = settings.layerId ?? "selection-polygon";
        this.minZoom = settings.minZoom ?? 0;
        // hook up events.
        this.map.on("initialized", (_) => {
            this.map.on("load", (_) => this._onMapLoad());
            //this.map.on("sourcedata", (e) => this._onMapSourceData(e));
            this.map.on("moveend", (e) => this._onMapMoveEnd());
            this.map.on("idle", (e) => this._onMapIdle());
        });
        this.enabled.subscribe(value => {
            if (value) {
                this.enable();
            }
            else {
                this.disable();
            }
        });
        this.enabled.set(enabled);
    }
    getEnabledStore() {
        return this.enabled;
    }
    getSelectionStore() {
        return this.selection;
    }
    enable() {
        if (get(this.enabled))
            return;
        if (!this.layersCreated) {
            this.createLayers();
        }
        else {
            const source = this.map.getSource(this.layerId + "-data");
            this.map.setLayoutProperty(this.layerId, "visibility", "visible");
            source.setData(turf.featureCollection([]));
            this._setSelectionCircleFromView();
        }
        this.selectionDirty = true;
    }
    disable() {
        if (!get(this.enabled))
            return;
        const source = this.map.getSource(this.layerId + "-data");
        if (typeof source === "undefined")
            return; // map not loaded yet.
        this.map.setLayoutProperty(this.layerId, "visibility", "visible");
        source.setData(turf.featureCollection([]));
    }
    createLayers() {
        if (this.layersCreated)
            return;
        if (!this.mapLoaded)
            return;
        this.layersCreated = true;
        // add layer to show polygon.
        this.map.addSource(this.layerId + "-data", {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: []
            }
        });
        this.map.addLayer({
            id: this.layerId,
            type: "line",
            minzoom: this.minZoom,
            source: this.layerId + "-data",
            layout: {
                visibility: "visible"
            },
            paint: {
                "line-color": "#ffc000",
                "line-dasharray": [1, 1],
                "line-width": 2
            }
        }, this.beforeType);
        this.selectionDirty = true;
    }
    _onMapLoad() {
        if (!get(this.enabled))
            return;
        this.createLayers();
        this._setSelectionCircleFromView();
    }
    _onMapMoveEnd() {
        if (!get(this.enabled))
            return;
        this.createLayers();
        this._setSelectionCircleFromView();
    }
    _onMapIdle() {
        if (!this.map.loaded())
            return;
        this.mapLoaded = true;
        if (!get(this.enabled))
            return;
        this.createLayers();
        if (!this.selectionDirty)
            return;
        this._setSelectionCircleFromView();
    }
    _setSelectionCircleFromView() {
        if (!this.layersCreated)
            return;
        if (!this.mapLoaded) {
            this.selectionDirty = true;
            return;
        }
        this.selectionDirty = false;
        if (this.map.getView().zoom < this.minZoom) {
            this.map.getSource(this.layerId + "-data").setData(turf.featureCollection([]));
            // notify listeners of updates.
            this.selection.set(undefined);
            return;
        }
        // get maplibre view.
        const mapContainer = this.map.getContainer();
        const boundingBox = {
            left: 0,
            top: 0,
            width: mapContainer.clientWidth,
            height: mapContainer.clientHeight
        };
        // determine radius and center.
        const sw = this.map.unproject([boundingBox.left, boundingBox.top + boundingBox.height]);
        const ne = this.map.unproject([boundingBox.left + boundingBox.width, boundingBox.top]);
        const bounds = new LngLatBounds(sw, ne);
        const center = bounds.getCenter();
        const height = (bounds.getNorth() - bounds.getSouth());
        const width = (bounds.getEast() - bounds.getWest());
        let radius = height / 2;
        if (radius > (width / 4)) {
            radius = width / 4;
        }
        if (radius > 0.5) {
            radius = 0.5;
        }
        // create polygon.
        // REMARK: https://github.com/Turfjs/turf/issues/1297
        const polygon = turf.circle(turf.point([center.lng, center.lat]), (radius) - (radius / 20), { units: "degrees" });
        this.map.getSource(this.layerId + "-data").setData(turf.featureCollection([polygon]));
        // notify listeners of updates.
        this.selection.set(polygon);
    }
}
